import { User } from '@/types'

/**
 * Audit logging system for role-based actions
 * Tracks user actions and permission checks for security and compliance
 */

export interface AuditLogEntry {
  id: string
  timestamp: Date
  userId: number
  userEmail: string
  userRole: string
  action: string
  resource: string
  resourceId?: string | number
  permission: string
  granted: boolean
  reason?: string
  metadata?: Record<string, any>
  ipAddress?: string
  userAgent?: string
}

export interface AuditLogFilter {
  userId?: number
  userRole?: string
  action?: string
  resource?: string
  permission?: string
  granted?: boolean
  startDate?: Date
  endDate?: Date
}

class AuditLogger {
  private logs: AuditLogEntry[] = []
  private maxLogs = 10000 // Keep last 10k entries

  /**
   * Log a permission check
   */
  logPermissionCheck(
    user: User | null,
    permission: string,
    resource: string,
    resourceId?: string | number,
    granted: boolean = true,
    reason?: string,
    metadata?: Record<string, any>
  ): void {
    if (!user) return

    const entry: AuditLogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      userId: user.id,
      userEmail: user.email,
      userRole: user.role,
      action: 'permission_check',
      resource,
      resourceId,
      permission,
      granted,
      reason,
      metadata,
      ipAddress: this.getClientIP(),
      userAgent: this.getUserAgent(),
    }

    this.addLog(entry)
  }

  /**
   * Log a user action
   */
  logUserAction(
    user: User | null,
    action: string,
    resource: string,
    resourceId?: string | number,
    metadata?: Record<string, any>
  ): void {
    if (!user) return

    const entry: AuditLogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      userId: user.id,
      userEmail: user.email,
      userRole: user.role,
      action,
      resource,
      resourceId,
      permission: 'action_performed',
      granted: true,
      metadata,
      ipAddress: this.getClientIP(),
      userAgent: this.getUserAgent(),
    }

    this.addLog(entry)
  }

  /**
   * Log a data access attempt
   */
  logDataAccess(
    user: User | null,
    resource: string,
    resourceId?: string | number,
    granted: boolean = true,
    reason?: string,
    metadata?: Record<string, any>
  ): void {
    if (!user) return

    const entry: AuditLogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      userId: user.id,
      userEmail: user.email,
      userRole: user.role,
      action: 'data_access',
      resource,
      resourceId,
      permission: 'data_read',
      granted,
      reason,
      metadata,
      ipAddress: this.getClientIP(),
      userAgent: this.getUserAgent(),
    }

    this.addLog(entry)
  }

  /**
   * Log a data modification attempt
   */
  logDataModification(
    user: User | null,
    action: string,
    resource: string,
    resourceId?: string | number,
    granted: boolean = true,
    reason?: string,
    metadata?: Record<string, any>
  ): void {
    if (!user) return

    const entry: AuditLogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      userId: user.id,
      userEmail: user.email,
      userRole: user.role,
      action,
      resource,
      resourceId,
      permission: 'data_write',
      granted,
      reason,
      metadata,
      ipAddress: this.getClientIP(),
      userAgent: this.getUserAgent(),
    }

    this.addLog(entry)
  }

  /**
   * Get audit logs with optional filtering
   */
  getLogs(filter?: AuditLogFilter): AuditLogEntry[] {
    let filteredLogs = [...this.logs]

    if (filter) {
      if (filter.userId) {
        filteredLogs = filteredLogs.filter(log => log.userId === filter.userId)
      }
      if (filter.userRole) {
        filteredLogs = filteredLogs.filter(log => log.userRole === filter.userRole)
      }
      if (filter.action) {
        filteredLogs = filteredLogs.filter(log => log.action === filter.action)
      }
      if (filter.resource) {
        filteredLogs = filteredLogs.filter(log => log.resource === filter.resource)
      }
      if (filter.permission) {
        filteredLogs = filteredLogs.filter(log => log.permission === filter.permission)
      }
      if (filter.granted !== undefined) {
        filteredLogs = filteredLogs.filter(log => log.granted === filter.granted)
      }
      if (filter.startDate) {
        filteredLogs = filteredLogs.filter(log => log.timestamp >= filter.startDate!)
      }
      if (filter.endDate) {
        filteredLogs = filteredLogs.filter(log => log.timestamp <= filter.endDate!)
      }
    }

    return filteredLogs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  }

  /**
   * Get audit logs for a specific user
   */
  getUserLogs(userId: number, limit: number = 100): AuditLogEntry[] {
    return this.logs
      .filter(log => log.userId === userId)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit)
  }

  /**
   * Get audit logs for a specific resource
   */
  getResourceLogs(resource: string, resourceId?: string | number, limit: number = 100): AuditLogEntry[] {
    return this.logs
      .filter(log => log.resource === resource && (!resourceId || log.resourceId === resourceId))
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit)
  }

  /**
   * Get permission denial logs
   */
  getDeniedLogs(limit: number = 100): AuditLogEntry[] {
    return this.logs
      .filter(log => !log.granted)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit)
  }

  /**
   * Clear old logs to prevent memory issues
   */
  clearOldLogs(): void {
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, this.maxLogs)
    }
  }

  /**
   * Export logs for external analysis
   */
  exportLogs(filter?: AuditLogFilter): string {
    const logs = this.getLogs(filter)
    return JSON.stringify(logs, null, 2)
  }

  /**
   * Get audit statistics
   */
  getAuditStats(): {
    totalLogs: number
    deniedLogs: number
    grantedLogs: number
    uniqueUsers: number
    uniqueResources: number
    topActions: Array<{ action: string; count: number }>
    topDeniedPermissions: Array<{ permission: string; count: number }>
  } {
    const totalLogs = this.logs.length
    const deniedLogs = this.logs.filter(log => !log.granted).length
    const grantedLogs = totalLogs - deniedLogs
    const uniqueUsers = new Set(this.logs.map(log => log.userId)).size
    const uniqueResources = new Set(this.logs.map(log => log.resource)).size

    // Top actions
    const actionCounts = this.logs.reduce((acc, log) => {
      acc[log.action] = (acc[log.action] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    const topActions = Object.entries(actionCounts)
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    // Top denied permissions
    const deniedPermissionCounts = this.logs
      .filter(log => !log.granted)
      .reduce((acc, log) => {
        acc[log.permission] = (acc[log.permission] || 0) + 1
        return acc
      }, {} as Record<string, number>)
    const topDeniedPermissions = Object.entries(deniedPermissionCounts)
      .map(([permission, count]) => ({ permission, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    return {
      totalLogs,
      deniedLogs,
      grantedLogs,
      uniqueUsers,
      uniqueResources,
      topActions,
      topDeniedPermissions,
    }
  }

  private addLog(entry: AuditLogEntry): void {
    this.logs.push(entry)
    this.clearOldLogs()
    
    // In development, also log to console
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Audit Log:', {
        user: `${entry.userEmail} (${entry.userRole})`,
        action: entry.action,
        resource: entry.resource,
        permission: entry.permission,
        granted: entry.granted,
        reason: entry.reason,
      })
    }
  }

  private generateId(): string {
    return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private getClientIP(): string {
    // In a real implementation, this would get the actual client IP
    return '127.0.0.1'
  }

  private getUserAgent(): string {
    return navigator.userAgent
  }
}

// Create singleton instance
export const auditLogger = new AuditLogger()

/**
 * Higher-order function to wrap permission checks with audit logging
 */
export const withAuditLog = <T extends any[], R>(
  permission: string,
  resource: string,
  fn: (user: User | null, ...args: T) => R
) => {
  return (user: User | null, ...args: T): R => {
    const result = fn(user, ...args)
    
    // Log the permission check
    auditLogger.logPermissionCheck(
      user,
      permission,
      resource,
      undefined,
      !!result,
      result ? 'Permission granted' : 'Permission denied'
    )
    
    return result
  }
}

/**
 * Hook for audit logging in React components
 */
export const useAuditLog = () => {
  return {
    logPermissionCheck: auditLogger.logPermissionCheck.bind(auditLogger),
    logUserAction: auditLogger.logUserAction.bind(auditLogger),
    logDataAccess: auditLogger.logDataAccess.bind(auditLogger),
    logDataModification: auditLogger.logDataModification.bind(auditLogger),
    getLogs: auditLogger.getLogs.bind(auditLogger),
    getUserLogs: auditLogger.getUserLogs.bind(auditLogger),
    getResourceLogs: auditLogger.getResourceLogs.bind(auditLogger),
    getDeniedLogs: auditLogger.getDeniedLogs.bind(auditLogger),
    getAuditStats: auditLogger.getAuditStats.bind(auditLogger),
    exportLogs: auditLogger.exportLogs.bind(auditLogger),
  }
}
